import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { createdOf, resolvedOf } from '../lib/jira-dates';
import { isDone } from '../lib/status';
import { fmtInt } from '../lib/format';
import { StatusChip } from './StatusChip';
import type { Worksheet } from 'exceljs';
import type { Issue } from '../types/issue';

const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}
const jiraUrl = (base: string, key: string) => `${base}/browse/${encodeURIComponent(key)}`;

// „YYYY-MM-DD" → Date (lokale Zeitzone). End-Datum wird als EXKLUSIVE Grenze
// via +1 Tag interpretiert, damit Tickets mit Resolved-Zeitstempel am End-Tag
// mitgezählt werden.
function toLocalDate(iso: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function defaultStart(): string { return toIso(addDays(new Date(), -21)); }
function defaultEnd(): string   { return toIso(new Date()); }

// JIRA liefert das Sprint-Feld je nach Export-Variante unterschiedlich:
//   * als eigene Spalte „Sprint" (häufigster Fall) oder „Custom field (Sprint)"
//   * als mehrere Spalten mit demselben Namen bei Multi-Sprint-Tickets
//   * oder als Java-ToString der Form
//     "com.atlassian.greenhopper.service.sprint.Sprint@...[name=Sprint 19,…]"
// Diese Funktion vereinheitlicht alle Varianten zu einer Liste reiner Sprint-Namen.
const SPRINT_FIELD_CANDIDATES = ['Sprint', 'Sprints', 'Custom field (Sprint)', 'Custom field (Sprints)'];
function sprintsOf(row: Issue): string[] {
  const out = new Set<string>();
  for (const k of SPRINT_FIELD_CANDIDATES) {
    const v = (row as Record<string, unknown>)[k];
    if (!v) continue;
    const arr = Array.isArray(v) ? v : [v];
    for (const x of arr) {
      const s = String(x ?? '').trim();
      if (!s) continue;
      const m = s.match(/\bname=([^,\]]+)/);
      out.add((m ? m[1] : s).trim());
    }
  }
  return [...out];
}

export function SprintAnalysis() {
  const allRows = useData(s => s.rows) as Issue[];
  const teamFilter = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);
  const jiraBase = useMemo(() => loadJiraBaseUrl(), []);

  const [startIso, setStartIso] = useState<string>(defaultStart());
  const [endIso,   setEndIso]   = useState<string>(defaultEnd());
  const [sprintLabel, setSprintLabel] = useState<string>('');
  const [sprintPick, setSprintPick] = useState<string | null>(null);

  const allTeams = useMemo(
    () => sortedByCount(countBy(allRows, 'Team Name')),
    [allRows]
  );

  // Alle Sprint-Namen aus dem Datenbestand, sortiert nach Häufigkeit.
  // Leer, wenn das Sprint-Feld im CSV-Export nicht enthalten war.
  const allSprints = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of allRows) {
      for (const s of sprintsOf(r)) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [allRows]);
  const sprintFieldAvailable = allSprints.length > 0;

  const window = useMemo(() => {
    const s = toLocalDate(startIso);
    const e = toLocalDate(endIso);
    if (!s || !e) return null;
    return { start: s, endExcl: addDays(e, 1) };
  }, [startIso, endIso]);

  const stats = useMemo(() => {
    if (!window) return null;
    type TypeStat = { total: number; closed: number; resolved: number; aborted: number; cps: number; createdInSprint: number };
    type TeamStat = { total: number; byType: Record<string, number> };
    const perType: Record<string, TypeStat> = {};
    const perTeam: Record<string, TeamStat> = {};
    let total = 0;
    let bugsTotal = 0, bugsCps = 0, bugsInternal = 0, bugsCreatedInSprint = 0;
    const detail: { row: Issue; type: string; status: string; team: string; resolved: Date | null; created: Date | null; cps: string }[] = [];

    for (const r of allRows) {
      const status = String(r['Status'] ?? '').trim().toLowerCase();
      if (!isDone(status)) continue;
      const resolved = resolvedOf(r);
      // Modus A — Sprint-Zugehörigkeit (JIRA-Feld): Zeitfenster wird ignoriert
      // Modus B — Zeitfenster über Resolved-Datum
      if (sprintPick) {
        if (!sprintsOf(r).includes(sprintPick)) continue;
      } else {
        if (!resolved || resolved < window.start || resolved >= window.endExcl) continue;
      }
      const team = String(r['Team Name'] ?? '(ohne Team)');
      if (teamFilter && team !== teamFilter) continue;

      const type = String(r['Issue Type'] ?? '?');
      const created = createdOf(r);
      const cpsRaw  = (r as Record<string, unknown>)['Custom field (CPS Ticket ID)'];
      const cpsVal  = Array.isArray(cpsRaw) ? (cpsRaw[0] ?? '') : (cpsRaw ?? '');
      const cps     = String(cpsVal ?? '').trim();

      total++;
      let pt = perType[type];
      if (!pt) { pt = { total: 0, closed: 0, resolved: 0, aborted: 0, cps: 0, createdInSprint: 0 }; perType[type] = pt; }
      pt.total++;
      if (status === 'closed')   pt.closed++;
      if (status === 'resolved') pt.resolved++;
      if (status === 'aborted')  pt.aborted++;
      if (cps) pt.cps++;
      if (created && created >= window.start && created < window.endExcl) pt.createdInSprint++;

      let pm = perTeam[team];
      if (!pm) { pm = { total: 0, byType: {} }; perTeam[team] = pm; }
      pm.total++;
      pm.byType[type] = (pm.byType[type] ?? 0) + 1;

      if (type === 'Bug') {
        bugsTotal++;
        if (cps) bugsCps++; else bugsInternal++;
        if (created && created >= window.start && created < window.endExcl) bugsCreatedInSprint++;
      }

      detail.push({ row: r, type, status, team, resolved, created, cps });
    }

    detail.sort((a, b) => (b.resolved?.getTime() ?? 0) - (a.resolved?.getTime() ?? 0));

    return { total, bugsTotal, bugsCps, bugsInternal, bugsCreatedInSprint, perType, perTeam, detail };
  }, [allRows, window, teamFilter, sprintPick]);

  function applyPreset(days: number) {
    const end = new Date();
    const start = addDays(end, -days + 1);
    setStartIso(toIso(start));
    setEndIso(toIso(end));
  }

  async function exportXlsx() {
    if (!stats || !window) return;
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'SchueCal Dashboard';
    wb.created = new Date();

    const COLORS = {
      headerBg: 'FF1E3A8A', headerFg: 'FFFFFFFF',
      subBg:    'FFE0E7FF', subFg:    'FF1E3A8A',
      metaBg:   'FFF3F4F6', metaFg:   'FF374151',
      colBg:    'FF334155', colFg:    'FFFFFFFF',
      zebra:    'FFF8FAFC', border:   'FFCBD5E1',
      linkFg:   'FF1D4ED8',
      bug:      'FFC62828', story: 'FF2E7D32', epic: 'FF7B1FA2', task: 'FF1976D2',
      cps:      'FFF59E0B',
    };
    const typeColor = (t: string) =>
      t === 'Bug' ? COLORS.bug :
      t === 'Story' ? COLORS.story :
      t === 'Epic' ? COLORS.epic :
      t === 'Task' ? COLORS.task : 'FF9E9E9E';

    const band = (ws: Worksheet, row: number, cols: number, bg: string, fg: string, text: string, size = 11, bold = true, h = 24) => {
      ws.mergeCells(row, 1, row, cols);
      const c = ws.getCell(row, 1);
      c.value = text;
      c.font = { name: 'Calibri', size, bold, color: { argb: fg } };
      c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      ws.getRow(row).height = h;
    };

    const wsOv = wb.addWorksheet('Übersicht');
    wsOv.columns = [{ width: 28 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }];
    const titleSuffix = sprintPick ? ` · ${sprintPick}` : sprintLabel ? ` · ${sprintLabel}` : '';
    const modeHint = sprintPick
      ? `Modus: Sprint-Feld (JIRA). Zählt alle Tickets mit Status = done, die laut JIRA zu „${sprintPick}" gehören — unabhängig vom Resolved-Datum.`
      : 'Modus: Zeitraum. Zählt alle Tickets mit Status = done (closed / resolved / aborted), deren Resolution-Datum im Zeitfenster liegt. ' +
        'Carry-Over aus früheren Sprints ist enthalten; „Created im Sprint" zeigt den Anteil frisch im Fenster entstandener Tickets.';
    band(wsOv, 1, 6, COLORS.headerBg, COLORS.headerFg,
      `SchueCal · Sprint-Auswertung${titleSuffix}`, 16, true, 32);
    band(wsOv, 2, 6, COLORS.subBg, COLORS.subFg, modeHint, 10.5, false, 48);
    band(wsOv, 3, 6, COLORS.metaBg, COLORS.metaFg,
      `Team: ${teamFilter || 'Alle Teams'}     ·     ${sprintPick ? `Sprint: ${sprintPick}` : `Zeitraum: ${startIso} – ${endIso}`}     ·     Σ Tickets: ${stats.total}     ·     Erstellt: ${new Date().toLocaleString('de-DE')}`,
      10, true, 20);
    wsOv.getRow(4).height = 6;

    const hd = wsOv.getRow(5);
    ['Issue Type', 'Σ', 'closed', 'resolved', 'aborted', 'davon CPS'].forEach((t, i) => {
      const c = hd.getCell(i + 1);
      c.value = t;
      c.font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.colFg } };
      c.alignment = { vertical: 'middle', horizontal: i === 0 ? 'left' : 'right', indent: 1 };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.colBg } };
    });
    hd.height = 22;

    const typeEntries = Object.entries(stats.perType).sort((a, b) => b[1].total - a[1].total);
    typeEntries.forEach(([type, v], i) => {
      const row = wsOv.getRow(6 + i);
      row.getCell(1).value = type;
      row.getCell(2).value = v.total;
      row.getCell(3).value = v.closed;
      row.getCell(4).value = v.resolved;
      row.getCell(5).value = v.aborted;
      row.getCell(6).value = v.cps;
      for (let c = 1; c <= 6; c++) {
        const cell = row.getCell(c);
        cell.font = { name: 'Calibri', size: 10.5, bold: c === 2 };
        cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'left' : 'right', indent: 1 };
        if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebra } };
      }
      row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: typeColor(type) } };
      row.getCell(1).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
      row.height = 20;
    });

    const wsTeam = wb.addWorksheet('Teams');
    const typeKeys = typeEntries.map(([t]) => t);
    wsTeam.columns = [{ width: 24 }, { width: 10 }, ...typeKeys.map(() => ({ width: 10 }))];
    band(wsTeam, 1, 2 + typeKeys.length, COLORS.headerBg, COLORS.headerFg, 'Teams × Issue-Type', 14, true, 28);
    band(wsTeam, 2, 2 + typeKeys.length, COLORS.metaBg, COLORS.metaFg,
      `Zeitraum: ${startIso} – ${endIso}  ·  nur Status=done`, 10, true, 20);
    wsTeam.getRow(3).height = 6;
    const th = wsTeam.getRow(4);
    ['Team', 'Σ', ...typeKeys].forEach((t, i) => {
      const c = th.getCell(i + 1);
      c.value = t;
      c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.colFg } };
      c.alignment = { vertical: 'middle', horizontal: i === 0 ? 'left' : 'right', indent: 1 };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.colBg } };
    });
    th.height = 22;
    Object.entries(stats.perTeam).sort((a, b) => b[1].total - a[1].total).forEach(([team, v], i) => {
      const row = wsTeam.getRow(5 + i);
      row.getCell(1).value = team;
      row.getCell(2).value = v.total;
      typeKeys.forEach((tk, j) => { row.getCell(3 + j).value = v.byType[tk] ?? 0; });
      for (let c = 1; c <= 2 + typeKeys.length; c++) {
        const cell = row.getCell(c);
        cell.font = { name: 'Calibri', size: 10.5, bold: c === 2 };
        cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'left' : 'right', indent: 1 };
        if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebra } };
      }
      row.height = 20;
    });
    wsTeam.views = [{ state: 'frozen', ySplit: 4 }];

    const wsT = wb.addWorksheet('Tickets');
    wsT.columns = [
      { width: 14 }, { width: 16 }, { width: 10 }, { width: 14 }, { width: 12 },
      { width: 12 }, { width: 12 }, { width: 60 },
    ];
    band(wsT, 1, 8, COLORS.headerBg, COLORS.headerFg, 'Tickets · Detail', 14, true, 28);
    band(wsT, 2, 8, COLORS.metaBg, COLORS.metaFg,
      `Team: ${teamFilter || 'Alle Teams'}  ·  Zeitraum: ${startIso} – ${endIso}  ·  ${stats.total} Tickets, absteigend nach Resolved-Datum`,
      10, true, 20);
    wsT.getRow(3).height = 6;
    ['Key', 'Team', 'Type', 'Status', 'Resolved', 'Created', 'CPS?', 'Summary'].forEach((t, i) => {
      const c = wsT.getRow(4).getCell(i + 1);
      c.value = t;
      c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.colFg } };
      c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.colBg } };
    });
    wsT.getRow(4).height = 22;
    const browse = (k: string) => `${jiraBase}/browse/${encodeURIComponent(k)}`;
    stats.detail.forEach((d, i) => {
      const row = wsT.getRow(5 + i);
      const key = String(d.row['Issue key'] ?? '');
      row.getCell(1).value = key ? { text: key, hyperlink: browse(key), tooltip: 'In JIRA öffnen' } : '';
      row.getCell(2).value = d.team;
      row.getCell(3).value = d.type;
      row.getCell(4).value = d.status;
      row.getCell(5).value = d.resolved ?? '';
      row.getCell(5).numFmt = 'dd.mm.yyyy';
      row.getCell(6).value = d.created ?? '';
      row.getCell(6).numFmt = 'dd.mm.yyyy';
      row.getCell(7).value = d.cps ? 'ja' : '';
      row.getCell(8).value = String(d.row['Summary'] ?? '');
      const zebra = i % 2 === 1;
      for (let c = 1; c <= 8; c++) {
        const cell = row.getCell(c);
        cell.font = { name: 'Calibri', size: 10 };
        cell.alignment = { vertical: 'top', horizontal: c === 1 ? 'left' : c === 5 || c === 6 ? 'right' : 'left', wrapText: c === 8 };
        cell.border = { bottom: { style: 'hair', color: { argb: COLORS.border } } };
        if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebra } };
      }
      row.getCell(1).font = { name: 'Calibri', size: 10, bold: true, color: { argb: COLORS.linkFg }, underline: true };
      row.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: typeColor(d.type) } };
      row.getCell(3).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      if (d.cps) {
        row.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.cps } };
        row.getCell(7).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      }
      row.height = 24;
    });
    wsT.views = [{ state: 'frozen', ySplit: 4 }];
    wsT.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4 + stats.detail.length, column: 8 } };

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = [sprintPick || sprintLabel || `${startIso}_${endIso}`, teamFilter || 'alle-teams']
      .filter(Boolean).join('_').replace(/[^\w-]+/g, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `schuecal-sprint_${slug}_${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5, color: 'text.secondary' }}>TEAM</Typography>
        <Chip size="small" label="Alle"
          color={teamFilter === '' ? 'primary' : 'default'}
          variant={teamFilter === '' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ team: '' })} clickable />
        {allTeams.map(([name, count]) => (
          <Chip key={name} size="small" label={`${name} (${count})`}
            color={teamFilter === name ? 'primary' : 'default'}
            variant={teamFilter === name ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: name })} clickable />
        ))}
      </Box>

      {sprintFieldAvailable && (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>SPRINT</Typography>
          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            options={allSprints.map(([name]) => name)}
            value={sprintPick}
            onChange={(_, v) => setSprintPick(v)}
            renderInput={(params) => <TextField {...params} label="Sprint auswählen (JIRA-Feld)" placeholder="z.B. Sprint 19" />}
            renderOption={(props, option) => {
              const n = allSprints.find(([k]) => k === option)?.[1] ?? 0;
              return <li {...props} key={option}>{option}<span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>· {n} Tickets</span></li>;
            }}
            isOptionEqualToValue={(a, b) => a === b}
          />
          {sprintPick && (
            <Chip size="small" color="primary" label={`Aktiv: ${sprintPick}`} onDelete={() => setSprintPick(null)} />
          )}
        </Stack>
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ opacity: sprintPick ? 0.5 : 1, pointerEvents: sprintPick ? 'none' : 'auto' }}
      >
        <TextField type="date" size="small" label="Von"
          value={startIso} onChange={e => setStartIso(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
        <TextField type="date" size="small" label="Bis (inkl.)"
          value={endIso} onChange={e => setEndIso(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
        <TextField size="small" label="Sprint-Bezeichnung (optional)"
          placeholder="z.B. Sprint 19"
          value={sprintLabel} onChange={e => setSprintLabel(e.target.value)}
          sx={{ minWidth: 200 }} />
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => applyPreset(14)}>2 W</Button>
          <Button onClick={() => applyPreset(21)}>3 W</Button>
          <Button onClick={() => applyPreset(30)}>30 T</Button>
          <Button onClick={() => applyPreset(90)}>90 T</Button>
        </ButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
          disabled={!stats || stats.total === 0} onClick={exportXlsx}>
          Excel-Export{stats ? ` (${fmtInt(stats.total)})` : ''}
        </Button>
      </Stack>

      <Alert
        severity={sprintPick ? 'success' : sprintFieldAvailable ? 'info' : 'warning'}
        sx={{ py: 0.5 }}
      >
        {sprintPick
          ? `Modus: Sprint-Feld (JIRA). Zählt Tickets mit Status = done, die laut JIRA zu „${sprintPick}" gehören. Zeitraum-Picker ist in diesem Modus deaktiviert.`
          : sprintFieldAvailable
          ? 'Modus: Zeitraum. Zählt Tickets mit Status = done, deren Resolved-Datum im Fenster liegt — inkl. Carry-Over aus früheren Sprints. Alternativ oben einen Sprint auswählen für exakte JIRA-Zuordnung.'
          : 'Modus: Zeitraum (approximiert). Das JIRA-Feld „Sprint" fehlt im Import — beim nächsten JIRA-CSV-Export das Feld „Sprint" mit auswählen, dann erscheint hier ein Dropdown für die exakte Sprint-Zuordnung.'}
      </Alert>

      {stats && stats.total === 0 && (
        <Alert severity="warning">Keine Tickets im Zeitraum. Team-/Datum-Filter prüfen.</Alert>
      )}

      {stats && stats.total > 0 && (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Kpi title="Σ Tickets geschlossen" value={fmtInt(stats.total)} subtitle={`${startIso} bis ${endIso}`} accent="#1976d2" />
            <Kpi title="Bugs" value={fmtInt(stats.bugsTotal)} subtitle={`davon ${fmtInt(stats.bugsCps)} Kunden-Bugs (CPS)`} accent="#c62828" />
            <Kpi title="Bugs intern" value={fmtInt(stats.bugsInternal)} subtitle="ohne CPS-Referenz" accent="#ef6c00" />
            <Kpi title="Im Sprint erstellt + geschlossen" value={fmtInt(stats.bugsCreatedInSprint)} subtitle={`von ${fmtInt(stats.bugsTotal)} Bugs (Rest = Carry-Over)`} accent="#2e7d32" />
          </Stack>

          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Nach Issue-Type
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Σ</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>closed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>resolved</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>aborted</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>CPS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>im Sprint created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(stats.perType).sort((a, b) => b[1].total - a[1].total).map(([type, v]) => (
                    <TableRow key={type} hover>
                      <TableCell><Chip size="small" label={type} sx={{ fontWeight: 700 }} /></TableCell>
                      <TableCell align="right"><b>{fmtInt(v.total)}</b></TableCell>
                      <TableCell align="right">{fmtInt(v.closed)}</TableCell>
                      <TableCell align="right">{fmtInt(v.resolved)}</TableCell>
                      <TableCell align="right">{fmtInt(v.aborted)}</TableCell>
                      <TableCell align="right">{fmtInt(v.cps)}</TableCell>
                      <TableCell align="right">{fmtInt(v.createdInSprint)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {!teamFilter && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Nach Team (Klick setzt globalen Team-Filter)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Σ</TableCell>
                      {Object.keys(stats.perType).sort().map(t => (
                        <TableCell key={t} align="right" sx={{ fontWeight: 700 }}>{t}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(stats.perTeam).sort((a, b) => b[1].total - a[1].total).map(([team, v]) => (
                      <TableRow key={team} hover sx={{ cursor: 'pointer' }} onClick={() => setFilters({ team })}>
                        <TableCell>{team}</TableCell>
                        <TableCell align="right"><b>{fmtInt(v.total)}</b></TableCell>
                        {Object.keys(stats.perType).sort().map(t => (
                          <TableCell key={t} align="right">{fmtInt(v.byType[t] ?? 0)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Tickets · {fmtInt(stats.detail.length)} Treffer, max. 500 angezeigt
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Resolved</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CPS</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.detail.slice(0, 500).map(d => {
                    const key = String(d.row['Issue key'] ?? '');
                    return (
                      <TableRow key={key} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          <Link href={jiraUrl(jiraBase, key)} target="_blank" rel="noopener noreferrer" underline="hover"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            {key}
                            <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                          </Link>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{d.team}</TableCell>
                        <TableCell>
                          <Chip size="small" label={d.type} variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
                        </TableCell>
                        <TableCell><StatusChip status={d.status} /></TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{d.resolved ? d.resolved.toLocaleDateString('de-DE') : '–'}</TableCell>
                        <TableCell>
                          {d.cps && <Chip size="small" label="CPS" sx={{ height: 18, fontSize: 10, bgcolor: '#f59e0b', color: '#fff', fontWeight: 700 }} />}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 500 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(d.row['Summary'] ?? '')}>
                            {String(d.row['Summary'] ?? '')}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}

function Kpi({ title, value, subtitle, accent }: { title: string; value: string; subtitle?: string; accent?: string }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>{title}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: accent ?? 'text.primary', lineHeight: 1.1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{subtitle}</Typography>
        )}
      </CardContent>
    </Card>
  );
}
